import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileSignature, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { api } from "../../lib/api";
import { POLL_INTERVAL_MS, MAX_POLL_ATTEMPTS } from "./preSignatureUploadPolling";
import { ELIGIBLE_TYPES, eligibleTypesLabel } from "../../pages/preSignatureRows";
import { toast } from "sonner";
import { usePlan } from "../../lib/usePlan";
import { runAnalysis } from "../../pages/PreSignature";
import { Button } from "../ui/button";

const MAX_BYTES = 25 * 1024 * 1024; // 25 Mo — matches the backend hard limit (documents.py:337-338)
// Polling budget lives in its own module so a test can assert it (see
// preSignatureUploadPolling.js for the measured rationale).

/**
 * Minimal direct-upload entry point for pre-signature documents.
 *
 * Deliberately reduced scope (karpathy guidance): one file input, one
 * button, one loading state. No drag & drop, no multi-file, no shared
 * upload hook — this is intentionally NOT DocumentsDropZone, do not merge
 * the two or replicate its .docx/.xlsx/50MB gap (backend is PDF-only, 25MB).
 *
 * Pre-classification: POST /documents/detect-type with {filename} pre-fills
 * document_type (zero new classification logic — the returned type, always
 * one of KNOWN_TYPES, is used verbatim). The upload itself is then tagged
 * document_category="pre_signature" so downstream consumers can identify it.
 *
 * Full flow: upload -> POST /documents/{id}/analyze -> poll GET status ->
 * on "completed", fire exactly one `should_i_sign` decision -> navigate to
 * /decisions/{id}. Two independent guards protect the LLM-costed
 * /decisions call:
 *   1. `runIdRef` — a monotonically increasing generation token. Every
 *      async continuation (after each `await`) checks it against the id it
 *      captured when the run started; a mismatch means the run was
 *      cancelled (unmount) or superseded, so the continuation drops itself
 *      before touching state, navigating, or calling any endpoint.
 *   2. `decisionFiredRef` — a synchronous check-and-set (no `await` between
 *      read and write) guaranteeing /decisions is posted at most once per
 *      run even if two callers reach `runDecision` in the same tick.
 * Polling uses recursive `setTimeout` (not `setInterval`) so at most one
 * status request is ever in flight — the next poll is only scheduled once
 * the previous one has resolved, which removes tick-overlap at the root
 * instead of merely guarding against it.
 * `activeRef` blocks re-entry: a second file cannot be picked while a run
 * is in flight (the raw `<input>` is also disabled while busy).
 */
export default function PreSignatureUpload() {
  const { t } = useTranslation("pre_signature");
  const { canAccess } = usePlan();
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [phase, setPhase] = useState(null); // null | "uploading" | "analyzing" | "deciding"

  const pollTimerRef = useRef(null);
  const decisionFiredRef = useRef(false); // quota guard: at most one POST /decisions per run
  const runIdRef = useRef(0);             // generation token — bumped to cancel any in-flight run
  const activeRef = useRef(false);        // re-entry guard: only one run at a time

  const stopPolling = () => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  };

  // On unmount: bump the generation token so any in-flight request's
  // continuation (which cannot itself be aborted) drops silently instead
  // of touching state or navigating, and stop any pending poll timer.
  useEffect(() => () => {
    runIdRef.current += 1;
    stopPolling();
  }, []);

  // Entitlement gate — pre_signature is Sentinel-only. Hidden by default
  // (below) so an unentitled user never sees the trigger; handleFile also
  // re-checks this synchronously (not just via this early return) so a
  // stale closure or a re-render race can't reach the upload/decision
  // calls either. See PreSignatureLauncher.jsx for the established pattern.
  if (!canAccess("pre_signature")) return null;

  // Ends the current run with a toast. `myRun` is checked first — if the
  // run was superseded/cancelled since it started, this is a no-op so no
  // state update reaches an unmounted or already-reset component.
  const finish = (message, myRun, toastFn = toast.error) => {
    if (runIdRef.current !== myRun) return;
    toastFn(message);
    setBusy(false);
    setPhase(null);
    activeRef.current = false;
  };

  const runDecision = async (docId, myRun) => {
    if (decisionFiredRef.current) return; // one-shot guard, checked synchronously before any await
    decisionFiredRef.current = true;
    setPhase("deciding");
    try {
      const decision = await runAnalysis(docId);
      if (runIdRef.current !== myRun) return; // cancelled while awaiting — drop, no navigate
      setBusy(false);
      setPhase(null);
      activeRef.current = false;
      navigate(`/decisions/${decision.id}`);
    } catch (e) {
      finish(e.response?.data?.detail || t("upload.decision_failed"), myRun);
    }
  };

  // Recursive setTimeout — never more than one status request in flight.
  const pollTick = async (docId, myRun, attempt) => {
    if (runIdRef.current !== myRun) return; // cancelled before this tick started
    let status;
    try {
      const res = await api.get(`/documents/${docId}/status`);
      status = res.data?.status;
    } catch {
      status = null; // transient poll error — falls through to the cap check below
    }
    if (runIdRef.current !== myRun) return; // cancelled while the request was in flight

    if (status === "completed") {
      runDecision(docId, myRun);
      return;
    }
    // needs_review is terminal but degraded (extraction fell back — see
    // job_queue.py:200). should_i_sign would reason over unreliable
    // extraction, so we deliberately stop rather than spend LLM quota on
    // it; the user is told explicitly instead of waiting out the full cap.
    if (status === "needs_review") {
      finish(t("upload.needs_review"), myRun, toast.warning);
      return;
    }
    if (status === "failed" || status === "error") {
      finish(t("upload.analysis_failed"), myRun);
      return;
    }
    if (attempt >= MAX_POLL_ATTEMPTS) {
      finish(t("upload.analysis_timeout"), myRun);
      return;
    }
    pollTimerRef.current = setTimeout(() => pollTick(docId, myRun, attempt + 1), POLL_INTERVAL_MS);
  };

  const handleFile = async (file) => {
    if (!file) return;
    // Defense in depth: the module-level `if (!canAccess(...)) return null`
    // above already unmounts this component for unentitled users, but that
    // guard alone would not survive a stale closure holding a reference to
    // this handler — re-check synchronously before any request goes out.
    if (!canAccess("pre_signature")) return;
    if (activeRef.current) {
      toast.error(t("upload.busy"));
      return;
    }
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast.error(t("upload.only_pdf"));
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error(t("upload.too_large"));
      return;
    }
    activeRef.current = true;
    decisionFiredRef.current = false; // reset the quota guard for this new run
    const myRun = ++runIdRef.current;
    setBusy(true);
    setPhase("uploading");

    // Best-effort pre-classification — on failure the backend's own
    // "lease" default takes over.
    let documentType = "lease";
    try {
      const detect = await api.post("/documents/detect-type", { filename: file.name });
      if (runIdRef.current !== myRun) return;
      // Reject an ineligible type BEFORE uploading, so the user is not charged
      // an analysis for a document the feature cannot advise on. Same three
      // types the on-page Launcher accepts (ELIGIBLE_TYPES) — the two entry
      // points must not disagree about what is analysable.
      //
      // Only act on a real detection: with no text_preview sent, detect-type is
      // filename-only, and an uninformative filename returns
      // {type:"other", confidence:0, method:"fallback"} (classifier.py). Acting
      // on that would reject every file whose name gives no hint, so a fallback
      // is allowed through and the LLM classification decides later.
      const detected = detect.data?.type;
      if (detect.data?.method !== "fallback" && !ELIGIBLE_TYPES.includes(detected)) {
        finish(t("upload.ineligible_type", { types: eligibleTypesLabel(t) }), myRun);
        return;
      }
      if (runIdRef.current !== myRun) return;
      if (detect.data?.type) documentType = detect.data.type;
    } catch {
      if (runIdRef.current !== myRun) return;
      // ignore — upload proceeds with the default type
    }

    let docId;
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("document_type", documentType);
      form.append("document_category", "pre_signature");
      const uploadRes = await api.post("/documents/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (runIdRef.current !== myRun) return;
      docId = uploadRes.data?.id;
    } catch (e) {
      finish(e.response?.data?.detail || t("upload.upload_failed"), myRun);
      return;
    }

    try {
      setPhase("analyzing");
      await api.post(`/documents/${docId}/analyze`);
      if (runIdRef.current !== myRun) return;
    } catch (e) {
      finish(e.response?.data?.detail || t("upload.analyze_failed"), myRun);
      return;
    }

    // Analysis is now queued/running server-side. Do NOT call /decisions
    // until polling observes status === "completed" — should_i_sign
    // consumes extraction/findings data that only exists after analysis.
    pollTick(docId, myRun, 1);
  };

  return (
    <span data-testid="pre-signature-upload" className="inline-flex">
      <input
        ref={inputRef}
        type="file"
        accept=".pdf"
        disabled={busy}
        className="sr-only"
        onChange={(e) => handleFile(e.target.files?.[0])}
        data-testid="pre-signature-upload-input"
      />
      <Button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={busy}
        data-testid="pre-signature-upload-button"
        className="bg-zinc-950 hover:bg-zinc-800 text-white gap-1.5 h-9"
      >
        {busy
          ? <Loader2 size={14} className="animate-spin" strokeWidth={1.75} />
          : <FileSignature size={14} strokeWidth={1.75} />}
        {busy ? t(`upload.phase_${phase}`) : t("upload.cta")}
      </Button>
    </span>
  );
}
